import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  Button,
  useTheme,
  alpha,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
  Divider
} from "@mui/material";
import { Link } from "react-router-dom";
import CoinCard from "../components/CoinCard";
import ApplicationForm from "../components/ApplicationForm";
import { motion } from "framer-motion";
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// UK coins data - Complete collection from your images folder
// Premium/Diamond coins marked with isPremium flag
const ukCoins = [
  // ========== PREMIUM & DIAMOND COINS (DISPLAYED FIRST) ==========
  {
    id: 417,
    name: "20oz Panda Gold Coin Necklace with Diamond Halo",
    year: "2024",
    grade: "MS70",
    gradeNum: 70,
    price: "$45,000 – $65,000",
    priceNum: 55000,
    image: "/assets/images/ukcoins/20OZ Panda Gold Coin Necklace with Diamond Halo 14k Yellow Gold For .png",
    description: "Exquisite 20oz gold panda coin set in a stunning 14k yellow gold necklace with diamond halo. The ultimate luxury investment piece featuring 20 troy ounces of pure gold with brilliant diamond accents.",
    metal: "Gold with Diamonds",
    category: "uk",
    mint: "Custom Luxury",
    era: "Modern",
    isPremium: true,
    isDiamond: true
  },
  {
    id: 418,
    name: "Pure Gold Diamond-Shaped Coin – Forevermark",
    year: "2024",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$8,500 – $12,500",
    priceNum: 10500,
    image: "/assets/images/ukcoins/puregold.png",
    description: "Stunning diamond-shaped pure gold coin with Forevermark Black Label round diamond. The perfect fusion of precious metal and gemstone featuring a certified Forevermark diamond.",
    metal: "Gold with Diamond",
    category: "uk",
    mint: "Forevermark",
    era: "Modern",
    isPremium: true,
    isDiamond: true
  },

  // ========== GEORGIAN ERA (1714-1837) ==========
  {
    id: 401,
    name: "King George III Gold Sovereign",
    year: "1817",
    grade: "MS60",
    gradeNum: 60,
    price: "$4,500 – $7,500",
    priceNum: 6000,
    image: "/assets/images/ukcoins/1817 Gold Sovereign King George 3.png",
    description: "The first year of issue for the modern gold sovereign. Historic first-year sovereign with Pistrucci's St. George design. Extremely collectible with significant numismatic premium.",
    metal: "Gold",
    category: "uk",
    mint: "Royal Mint",
    era: "Georgian",
    isFirstYear: true
  },
  {
    id: 402,
    name: "King George IV Bare Head Gold Sovereign",
    year: "1829",
    grade: "MS62",
    gradeNum: 62,
    price: "$3,500 – $5,500",
    priceNum: 4500,
    image: "/assets/images/ukcoins/1829 Gold Sovereign King George IV Bare Head.png",
    description: "Rare George IV 'Bare Head' sovereign featuring the king in classical style. Limited mintage with strong collector demand.",
    metal: "Gold",
    category: "uk",
    mint: "Royal Mint",
    era: "Georgian"
  },
  {
    id: 403,
    name: "King William IV Gold Sovereign",
    year: "1831",
    grade: "AU55",
    gradeNum: 55,
    price: "$3,000 – $5,000",
    priceNum: 4000,
    image: "/assets/images/ukcoins/1831 Great Britain Gold Sovereign William.png",
    description: "Rare William IV sovereign from the short reign of the 'Sailor King' (1830-1837). Distinctive bare head portrait with strong collector value.",
    metal: "Gold",
    category: "uk",
    mint: "Royal Mint",
    era: "Georgian"
  },

  // ========== VICTORIAN ERA (1837-1901) ==========
  {
    id: 404,
    name: "Queen Victoria Young Head Gold Sovereign",
    year: "1875",
    grade: "AU55",
    gradeNum: 55,
    price: "$1,100 – $1,400",
    priceNum: 1250,
    image: "/assets/images/ukcoins/1875 Queen Victoria Young Head Gold Sovereign.png",
    description: "Victorian era gold sovereign featuring the classic 'Young Head' portrait of Queen Victoria. Popular with collectors and investors alike.",
    metal: "Gold",
    category: "uk",
    mint: "Royal Mint",
    era: "Victorian",
    portrait: "Young Head"
  },
  {
    id: 405,
    name: "Queen Victoria Young Head Sovereigns",
    year: "1875",
    grade: "MS61",
    gradeNum: 61,
    price: "$1,200 – $1,600",
    priceNum: 1400,
    image: "/assets/images/ukcoins/Queen Victoria Young Head Sovereigns.png",
    description: "Classic Victorian 'Young Head' sovereign in mint state condition. Beautiful example with original luster.",
    metal: "Gold",
    category: "uk",
    mint: "Royal Mint",
    era: "Victorian",
    portrait: "Young Head"
  },

  // ========== EDWARDIAN ERA (1901-1910) ==========
  {
    id: 406,
    name: "King Edward VII Gold Sovereign",
    year: "1902",
    grade: "MS64",
    gradeNum: 64,
    price: "$1,300 – $1,800",
    priceNum: 1550,
    image: "/assets/images/ukcoins/1902 King Edward VII Gold Sovereig.png",
    description: "Edward VII sovereign from his coronation year. Features the king in military uniform with the classic St George reverse.",
    metal: "Gold",
    category: "uk",
    mint: "Royal Mint",
    era: "Edwardian"
  },

  // ========== GEORGE V ERA (1910-1936) ==========
  {
    id: 407,
    name: "King George V Sovereigns",
    year: "1911-1932",
    grade: "MS62",
    gradeNum: 62,
    price: "$1,000 – $1,300",
    priceNum: 1150,
    image: "/assets/images/ukcoins/King George V Sovereigns (1911-1932) .png",
    description: "British gold sovereign featuring St. George and the dragon. George V sovereigns offer the best value for gold investors with minimal numismatic premium.",
    metal: "Gold",
    category: "uk",
    mint: "Royal Mint",
    era: "GeorgeV"
  },

  // ========== ELIZABETH II ERA (1952-2022) ==========
  {
    id: 408,
    name: "Elizabeth II Gold Sovereign",
    year: "1966",
    grade: "MS63",
    gradeNum: 63,
    price: "$950 – $1,150",
    priceNum: 1050,
    image: "/assets/images/ukcoins/1966 Great Britain Gold Sovereign Elizabeth II .png",
    description: "Mid-century Elizabeth II gold sovereign. Modern bullion sovereigns trade closest to melt value, making them excellent for pure gold investment.",
    metal: "Gold",
    category: "uk",
    mint: "Royal Mint",
    era: "Elizabethan"
  },
  {
    id: 409,
    name: "Elizabeth II 1989 Gold Sovereign Proof",
    year: "1989",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$1,500 – $2,200",
    priceNum: 1850,
    image: "/assets/images/ukcoins/1989 Gold Sovereign Elizabeth II Proof.png",
    description: "Special 500th anniversary of the sovereign. Flawless proof finish with original Royal Mint packaging. Highly collectible proof issue.",
    metal: "Gold",
    category: "uk",
    mint: "Royal Mint",
    era: "Elizabethan"
  },
  {
    id: 410,
    name: "Queen's Diamond Jubilee $300 Coin",
    year: "2012",
    grade: "Proof 69",
    gradeNum: 69,
    price: "$400 – $600",
    priceNum: 500,
    image: "/assets/images/ukcoins/2012 $300 Queen's Diamond Jubilee.png",
    description: "Commemorative $300 coin celebrating Queen Elizabeth II's Diamond Jubilee. Limited mintage issue.",
    metal: "Silver/Gold",
    category: "uk",
    mint: "Royal Mint",
    era: "Elizabethan"
  },
  {
    id: 411,
    name: "Diamond Jubilee Queen Elizabeth II",
    year: "2012",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$800 – $1,200",
    priceNum: 1000,
    image: "/assets/images/ukcoins/2012 DIAMOND JUBILEE QUEEN ELIZABETH.png",
    description: "Special Diamond Jubilee commemorative featuring Queen Elizabeth II. Marks 60 years on the throne.",
    metal: "Gold",
    category: "uk",
    mint: "Royal Mint",
    era: "Elizabethan"
  },
  {
    id: 412,
    name: "2012 Gold Proof Sovereign Diamond Jubilee",
    year: "2012",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$1,200 – $1,800",
    priceNum: 1500,
    image: "/assets/images/ukcoins/2012 Gold Proof Sovereign Diamond Jubilee.png",
    description: "Commemorating 60 years of Queen Elizabeth II's reign. Special diamond jubilee design with stunning proof finish.",
    metal: "Gold",
    category: "uk",
    mint: "Royal Mint",
    era: "Elizabethan"
  },
  {
    id: 413,
    name: "Diamond Jubilee 5 Pounds - Tristan da Cunha",
    year: "2012",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$600 – $900",
    priceNum: 750,
    image: "/assets/images/ukcoins/2012 PROOF QUEEN ELIZABETH II DIAMOND JUBILEE 5 POUNDS TRISTAN DA CUNHA.png",
    description: "Rare Tristan da Cunha 5 pounds proof coin celebrating Queen Elizabeth II's Diamond Jubilee. Exquisite detail and finish.",
    metal: "Silver/Gold",
    category: "uk",
    mint: "Tristan da Cunha",
    era: "Elizabethan"
  },
  {
    id: 414,
    name: "Elizabeth II 2021 Proof Full Sovereign",
    year: "2021",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$1,300 – $1,900",
    priceNum: 1600,
    image: "/assets/images/ukcoins/2021 Queen Elizabeth II Proof Full Sovereign .png",
    description: "Stunning proof sovereign from the final years of Queen Elizabeth II's reign. Perfect condition with original box and COA.",
    metal: "Gold",
    category: "uk",
    mint: "Royal Mint",
    era: "Elizabethan"
  },
  {
    id: 415,
    name: "Queen Elizabeth II Memorial Quarter Sovereign",
    year: "2022",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$500 – $700",
    priceNum: 600,
    image: "/assets/images/ukcoins/British Queen Elizabeth Memorial Quarter Sovereign Gold Coin .png",
    description: "Memorial quarter sovereign honoring Queen Elizabeth II. Final portrait of her reign. Limited edition proof issue.",
    metal: "Gold",
    category: "uk",
    mint: "Royal Mint",
    era: "Elizabethan"
  },

  // ========== MODERN ERA (2022-PRESENT) - CHARLES III ==========
  {
    id: 416,
    name: "King Charles III Gold Sovereign",
    year: "2024",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$1,100 – $1,500",
    priceNum: 1300,
    image: "/assets/images/ukcoins/2024 Sovereign Charles III Gold Coin - Lois Bullion.png",
    description: "Current year King Charles III sovereign. First portrait issues carry small collector premiums. Contains 0.2354 oz of fine gold.",
    metal: "Gold",
    category: "uk",
    mint: "Royal Mint",
    era: "Modern"
  }
];

const UKPage = () => {
  const theme = useTheme();
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [filter, setFilter] = useState('all');

  const handlePriceInquiry = (coin) => {
    setSelectedCoin(coin);
    setFormOpen(true);
  };

  // Country data with flag images - matching homepage
  const countries = [
    {
      name: "Australia/NZ",
      path: "/",
      flagImage: "/assets/images/australia.jpg",
      flagAlt: "Australian Flag",
      variant: "outlined"
    },
    {
      name: "Canada",
      path: "/canada",
      flagImage: "/assets/images/canada.jpg",
      flagAlt: "Canadian Flag",
      variant: "outlined"
    },
    {
      name: "USA",
      path: "/usa",
      flagImage: "/assets/images/usa.jpg",
      flagAlt: "USA Flag",
      variant: "outlined"
    },
    {
      name: "UK",
      path: "/uk",
      flagImage: "/assets/images/united kingdom.jpg",
      flagAlt: "UK Flag",
      variant: "contained"
    }
  ];

  // Get premium/diamond coins
  const premiumCoins = ukCoins.filter(c => c.isPremium === true);
  const regularCoins = ukCoins.filter(c => !c.isPremium);

  // Calculate stats
  const georgianCount = ukCoins.filter(c => c.era === 'Georgian').length;
  const victorianCount = ukCoins.filter(c => c.era === 'Victorian').length;
  const edwardianCount = ukCoins.filter(c => c.era === 'Edwardian').length;
  const georgeVCount = ukCoins.filter(c => c.era === 'GeorgeV').length;
  const elizabethanCount = ukCoins.filter(c => c.era === 'Elizabethan').length;
  const modernCount = ukCoins.filter(c => c.era === 'Modern').length;
  const diamondCount = ukCoins.filter(c => c.isDiamond === true).length;
  const proofCount = ukCoins.filter(c => c.grade?.includes('Proof')).length;

  const getFilteredCoins = () => {
    if (filter === 'all') {
      return ukCoins;
    }
    if (filter === 'premium') {
      return premiumCoins;
    }
    if (filter === 'georgian') {
      return ukCoins.filter(c => c.era === 'Georgian');
    }
    if (filter === 'victorian') {
      return ukCoins.filter(c => c.era === 'Victorian');
    }
    if (filter === 'edwardian') {
      return ukCoins.filter(c => c.era === 'Edwardian');
    }
    if (filter === 'georgev') {
      return ukCoins.filter(c => c.era === 'GeorgeV');
    }
    if (filter === 'elizabethan') {
      return ukCoins.filter(c => c.era === 'Elizabethan');
    }
    if (filter === 'modern') {
      return ukCoins.filter(c => c.era === 'Modern');
    }
    return ukCoins;
  };

  const filteredCoins = getFilteredCoins();

  return (
    <Box sx={{ 
      py: { xs: 8, md: 12 },
      backgroundColor: '#F5F5F5',
      position: 'relative',
      minHeight: '100vh',
    }}>
      {/* Decorative Lines - Matching Homepage */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
        }}
      />

      <Container maxWidth="xl">
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 4 }}>
          <MuiLink
            component={Link}
            to="/"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </MuiLink>
          <Typography color="primary">United Kingdom Collection</Typography>
        </Breadcrumbs>

        {/* Region Navigation with Flag Images - Matching Homepage */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <Stack 
            direction="row" 
            spacing={3} 
            flexWrap="wrap" 
            justifyContent="center"
            alignItems="flex-start"
            sx={{
              '& .MuiButton-root': {
                minWidth: { xs: '100px', sm: '120px' },
              }
            }}
          >
            {countries.map((country, index) => (
              <Box key={index} sx={{ textAlign: 'center', width: '120px' }}>
                <Box
                  sx={{
                    width: '60px',
                    height: '40px',
                    mb: 1.5,
                    mx: 'auto',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    backgroundColor: '#fff',
                    border: '1px solid rgba(0,0,0,0.1)',
                  }}
                >
                  <img 
                    src={country.flagImage} 
                    alt={country.flagAlt}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/60x40?text=Flag';
                    }}
                  />
                </Box>
                <Button
                  component={Link}
                  to={country.path}
                  variant={country.variant}
                  size="medium"
                  sx={{
                    px: 2,
                    py: 1.2,
                    width: '100%',
                    ...(country.variant === 'contained' && {
                      backgroundColor: 'primary.main',
                      color: '#000000',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    }),
                    ...(country.variant === 'outlined' && {
                      borderColor: alpha('#9E9E9E', 0.3),
                      color: '#616161',
                      '&:hover': {
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        backgroundColor: alpha('#C6A75E', 0.04),
                      },
                    }),
                    fontWeight: 600,
                    fontSize: '0.9rem',
                  }}
                >
                  {country.name}
                </Button>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Section Header - Matching Homepage Style */}
        <Box sx={{ mb: 8, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'primary.main',
                mb: 2,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                fontWeight: 400,
              }}
            >
              United Kingdom
            </Typography>
            
            <Typography variant="h2" sx={{ mb: 3, color: '#2D2D2D' }}>
              British Sovereigns
              <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>
                & Historic Gold
              </Box>
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                maxWidth: '700px', 
                mx: 'auto',
                color: '#616161',
                fontSize: '1.2rem',
              }}
            >
              Discover the timeless elegance of British gold sovereigns from the Royal Mint, featuring 
              the iconic St. George and the dragon design that has adorned these coins for over 200 years.
            </Typography>
          </motion.div>
        </Box>

        {/* Diamond Banner - Highlighting Premium Coins */}
        {filter === 'all' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Box 
              sx={{ 
                mb: 6, 
                p: 3, 
                background: `linear-gradient(135deg, ${alpha('#C6A75E', 0.1)} 0%, ${alpha('#C6A75E', 0.05)} 100%)`,
                border: '2px solid',
                borderColor: alpha('#C6A75E', 0.3),
                borderRadius: 3,
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ position: 'absolute', top: -20, right: -20, fontSize: '100px', opacity: 0.1 }}>
                💎
              </Box>
              <Typography variant="h5" sx={{ color: '#C6A75E', fontWeight: 600, mb: 1 }}>
                💎 Exclusive Diamond & Premium Collection 💎
              </Typography>
              <Typography variant="body1" sx={{ color: '#616161' }}>
                Featuring our most exquisite pieces with genuine diamonds and exceptional craftsmanship
              </Typography>
            </Box>
          </motion.div>
        )}

        {/* Filter Chips - Updated with all eras including Premium */}
        <Stack 
          direction="row" 
          spacing={2} 
          justifyContent="center" 
          sx={{ mb: 6 }}
          flexWrap="wrap"
          gap={1}
        >
          <Chip
            label={`All Coins (${ukCoins.length})`}
            onClick={() => setFilter('all')}
            color={filter === 'all' ? 'primary' : 'default'}
            sx={{ 
              fontSize: '0.9rem',
              py: 2,
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
          <Chip
            label={`💎 Premium (${diamondCount})`}
            onClick={() => setFilter('premium')}
            color={filter === 'premium' ? 'primary' : 'default'}
            icon={<span>💎</span>}
            sx={{ 
              fontSize: '0.9rem',
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
          <Chip
            label={`Georgian (${georgianCount})`}
            onClick={() => setFilter('georgian')}
            color={filter === 'georgian' ? 'primary' : 'default'}
            sx={{ 
              fontSize: '0.9rem',
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
          <Chip
            label={`Victorian (${victorianCount})`}
            onClick={() => setFilter('victorian')}
            color={filter === 'victorian' ? 'primary' : 'default'}
            sx={{ 
              fontSize: '0.9rem',
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
          <Chip
            label={`Edwardian (${edwardianCount})`}
            onClick={() => setFilter('edwardian')}
            color={filter === 'edwardian' ? 'primary' : 'default'}
            sx={{ 
              fontSize: '0.9rem',
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
          <Chip
            label={`George V (${georgeVCount})`}
            onClick={() => setFilter('georgev')}
            color={filter === 'georgev' ? 'primary' : 'default'}
            sx={{ 
              fontSize: '0.9rem',
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
          <Chip
            label={`Elizabethan (${elizabethanCount})`}
            onClick={() => setFilter('elizabethan')}
            color={filter === 'elizabethan' ? 'primary' : 'default'}
            sx={{ 
              fontSize: '0.9rem',
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
          <Chip
            label={`Modern (${modernCount})`}
            onClick={() => setFilter('modern')}
            color={filter === 'modern' ? 'primary' : 'default'}
            sx={{ 
              fontSize: '0.9rem',
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
        </Stack>

        {/* Quick Stats - Matching Homepage */}
        <Box 
          sx={{ 
            mb: 6, 
            display: 'flex', 
            justifyContent: 'center',
            gap: { xs: 3, sm: 5, md: 8 },
            flexWrap: 'wrap',
          }}
        >
          {[
            { label: 'Premium Diamond Pieces', value: `${diamondCount}`, icon: '💎' },
            { label: 'British Sovereigns', value: `${ukCoins.length - diamondCount}+` },
            { label: 'Proof Quality', value: `${proofCount}+` },
            { label: 'Certified Authentic', value: '100%' },
          ].map((stat, index) => (
            <Box 
              key={index} 
              sx={{ 
                textAlign: 'center',
                minWidth: { xs: '120px', sm: '140px' },
              }}
            >
              <Typography 
                variant="h4" 
                sx={{ 
                  color: 'primary.main', 
                  fontWeight: 600,
                  fontSize: { xs: '2rem', sm: '2.5rem' },
                }}
              >
                {stat.icon && <span style={{ fontSize: '2rem' }}>{stat.icon}</span>} {stat.value}
              </Typography>
              <Typography 
                variant="body2" 
                color="#616161"
                sx={{
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                }}
              >
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Results Count */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
          <Typography variant="body1" color="#616161">
            Showing {filteredCoins.length} {filter === 'premium' ? 'premium diamond' : 'British'} {filteredCoins.length === 1 ? 'coin' : 'coins'}
          </Typography>
        </Box>

        {/* Coins Grid - Premium coins displayed at top when showing all */}
        <Grid 
          container 
          spacing={4} 
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {/* If showing all coins, show premium first then regular */}
          {filter === 'all' ? (
            <>
              {/* Premium/Diamond Coins Section */}
              {premiumCoins.length > 0 && (
                <>
                  {premiumCoins.map((coin, index) => (
                    <Grid 
                      item 
                      xs={12} 
                      sm={6} 
                      md={4} 
                      key={`premium-${coin.id}`}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.05 }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <Box sx={{ position: 'relative', width: '100%' }}>
                          {/* Diamond Badge */}
                          <Box 
                            sx={{ 
                              position: 'absolute', 
                              top: -10, 
                              right: 20, 
                              zIndex: 10,
                              backgroundColor: '#C6A75E',
                              color: '#000',
                              px: 2,
                              py: 0.5,
                              borderRadius: 20,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              fontWeight: 'bold',
                              fontSize: '0.75rem',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                            }}
                          >
                            <span>💎</span> Diamond Collection
                          </Box>
                          <CoinCard 
                            coin={coin} 
                            onPriceInquiry={() => handlePriceInquiry(coin)}
                          />
                        </Box>
                      </motion.div>
                    </Grid>
                  ))}
                  
                  {/* Divider between premium and regular coins */}
                  {regularCoins.length > 0 && (
                    <Grid item xs={12}>
                      <Box 
                        sx={{ 
                          my: 4, 
                          textAlign: 'center',
                          position: 'relative'
                        }}
                      >
                        <Divider sx={{ mb: 3 }}>
                          <Chip 
                            label="Classic British Sovereigns" 
                            sx={{ 
                              backgroundColor: alpha('#C6A75E', 0.1),
                              color: '#C6A75E',
                              fontWeight: 600
                            }} 
                          />
                        </Divider>
                      </Box>
                    </Grid>
                  )}
                  
                  {/* Regular Coins */}
                  {regularCoins.map((coin, index) => (
                    <Grid 
                      item 
                      xs={12} 
                      sm={6} 
                      md={4} 
                      key={`regular-${coin.id}`}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: (premiumCoins.length + index) * 0.05 }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <CoinCard 
                          coin={coin} 
                          onPriceInquiry={() => handlePriceInquiry(coin)}
                        />
                      </motion.div>
                    </Grid>
                  ))}
                </>
              )}
            </>
          ) : (
            /* Normal display for filtered views */
            filteredCoins.map((coin, index) => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={4} 
                key={coin.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <CoinCard 
                    coin={coin} 
                    onPriceInquiry={() => handlePriceInquiry(coin)}
                  />
                </motion.div>
              </Grid>
            ))
          )}
        </Grid>

        {/* Information Box about Price Ranges - Matching Homepage */}
        <Box 
          sx={{ 
            mt: 6, 
            p: 3, 
            backgroundColor: alpha('#C6A75E', 0.05),
            border: '1px solid',
            borderColor: alpha('#C6A75E', 0.2),
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          <Typography variant="body2" color="#616161">
            <strong>Price Ranges:</strong> Prices shown are estimated market ranges based on current market conditions. 
            Final pricing depends on grade, certification, and market demand. Click "Inquire Now" for specific pricing and availability.
          </Typography>
        </Box>

        {/* Back Button */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Button
            component={Link}
            to="/"
            variant="outlined"
            size="large"
            startIcon={<ArrowBackIcon />}
            sx={{ 
              px: 6, 
              py: 1.5,
              borderColor: alpha('#9E9E9E', 0.3),
              color: '#616161',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
                backgroundColor: alpha('#C6A75E', 0.04),
              }
            }}
          >
            Back to Home
          </Button>
        </Box>
      </Container>

      {/* Application Form */}
      <ApplicationForm 
        open={formOpen}
        onClose={() => setFormOpen(false)}
        coin={selectedCoin}
      />
    </Box>
  );
};

export { ukCoins };
export default UKPage;